/* global console, document, Office, Excel, window */
import { FUNCTION_CATALOG, PRODUCT_TYPES } from "../functions/catalog";
import type { FunctionDoc } from "../functions/catalog";
import { login, logout, isLoggedIn, getLoggedInEmail } from "../shared/auth-service";

Office.onReady(() => {
  document.getElementById("sideload-msg").style.display = "none";
  document.getElementById("app-body").style.display = "block";
  renderCatalog();

  document.getElementById("btn-login").onclick = onLogin;
  document.getElementById("btn-logout").onclick = onLogout;
  document.getElementById("input-password").addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter") onLogin();
  });

  document.getElementById("function-catalog").addEventListener("click", onCatalogClick);

  // Expose helpers for manual debugging from the browser console.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).toggleFn = toggleFn;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).insertFormula = insertFormula;

  checkSession();
});

function renderCatalog(): void {
  const catalogRoot = document.getElementById("function-catalog");
  catalogRoot.innerHTML = "";
  FUNCTION_CATALOG.forEach((fn) => catalogRoot.appendChild(renderFunctionCard(fn)));

  const productRoot = document.getElementById("product-types");
  productRoot.innerHTML = "";
  PRODUCT_TYPES.forEach((productType) => {
    const chip = document.createElement("span");
    chip.className = "cog-tipo-chip";
    const name = document.createElement("strong");
    name.textContent = productType.name;
    const description = document.createElement("span");
    description.textContent = productType.description;
    chip.append(name, description);
    productRoot.appendChild(chip);
  });
}

function renderFunctionCard(fn: FunctionDoc): HTMLElement {
  const card = document.createElement("div");
  card.className = "cog-fn-card";

  const toggle = document.createElement("button");
  toggle.className = "cog-fn-toggle";
  toggle.type = "button";
  toggle.dataset.toggleFn = fn.id;

  const name = document.createElement("span");
  name.className = "cog-fn-name";
  name.textContent = fn.name;

  const tag = document.createElement("span");
  tag.className = `cog-fn-tag${fn.tagTone && fn.tagTone !== "green" ? ` cog-fn-tag--${fn.tagTone}` : ""}`;
  tag.textContent = fn.tag;

  const arrow = document.createElement("span");
  arrow.className = "cog-fn-arrow";
  arrow.id = `arr-${fn.id}`;
  arrow.textContent = "▾";

  toggle.append(name, tag, arrow);

  const body = document.createElement("div");
  body.className = "cog-fn-body";
  body.id = fn.id;
  body.style.display = "none";

  const desc = document.createElement("p");
  desc.className = "cog-fn-desc";
  desc.textContent = fn.description;

  const returns = document.createElement("div");
  returns.className = "cog-fn-return";
  const returnIcon = document.createElement("span");
  returnIcon.className = "cog-return-icon";
  returnIcon.textContent = "↩";
  const returnText = document.createElement("span");
  returnText.textContent = fn.returns;
  returns.append(returnIcon, returnText);

  body.append(desc, returns, renderParamsTable(fn), renderExamples(fn));
  card.append(toggle, body);
  return card;
}

function renderParamsTable(fn: FunctionDoc): HTMLTableElement {
  const table = document.createElement("table");
  table.className = "cog-params";

  const thead = document.createElement("thead");
  const headerRow = document.createElement("tr");
  ["Parametro", "Tipo", "Descricao"].forEach((title) => {
    const th = document.createElement("th");
    th.textContent = title;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);

  const tbody = document.createElement("tbody");
  fn.params.forEach((param) => {
    const tr = document.createElement("tr");
    const name = document.createElement("td");
    name.className = param.optional ? "opt" : "req";
    name.textContent = param.name;
    const type = document.createElement("td");
    type.textContent = param.type;
    const description = document.createElement("td");
    description.textContent = param.description;
    tr.append(name, type, description);
    tbody.appendChild(tr);
  });

  table.append(thead, tbody);
  return table;
}

function renderExamples(fn: FunctionDoc): HTMLElement {
  const fragment = document.createElement("div");

  const title = document.createElement("div");
  title.className = "cog-examples-title";
  title.textContent = "Exemplos";
  fragment.appendChild(title);

  fn.examples.forEach((example) => {
    const wrapper = document.createElement("div");
    wrapper.className = "cog-example";

    const label = document.createElement("div");
    label.className = "cog-example-label";
    label.textContent = example.label;

    const row = document.createElement("div");
    row.className = "cog-example-row";

    const code = document.createElement("code");
    code.textContent = example.formula;

    const button = document.createElement("button");
    button.className = "cog-insert";
    button.type = "button";
    button.dataset.formula = example.formula;
    button.textContent = "↗";
    button.title = "Inserir formula";

    row.append(code, button);
    wrapper.append(label, row);
    fragment.appendChild(wrapper);
  });

  return fragment;
}

function onCatalogClick(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  const toggleButton = target.closest<HTMLButtonElement>("[data-toggle-fn]");
  if (toggleButton?.dataset.toggleFn) {
    toggleFn(toggleButton.dataset.toggleFn);
    return;
  }

  const insertButton = target.closest<HTMLButtonElement>("[data-formula]");
  if (insertButton?.dataset.formula) {
    insertFormula(insertButton.dataset.formula);
  }
}

async function checkSession(): Promise<void> {
  const email = await getLoggedInEmail();
  const inputEmail = document.getElementById("input-email") as HTMLInputElement;
  if (email && !inputEmail.value) {
    inputEmail.value = email;
  }

  if (await isLoggedIn()) {
    showLoggedIn(email);
  }
}

async function onLogin(): Promise<void> {
  const email = (document.getElementById("input-email") as HTMLInputElement).value.trim();
  const password = (document.getElementById("input-password") as HTMLInputElement).value;

  if (!email || !password) {
    showStatus("Preencha e-mail e senha.", "error");
    return;
  }

  const btn = document.getElementById("btn-login") as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = "Entrando…";
  showStatus("Autenticando…", "info");

  try {
    await login(email, password);
    (document.getElementById("input-password") as HTMLInputElement).value = "";
    hideStatus();
    showLoggedIn(email);
  } catch (e) {
    console.error(e);
    showStatus(e instanceof Error ? e.message : "Erro ao autenticar.", "error");
  } finally {
    btn.disabled = false;
    btn.textContent = "Entrar";
  }
}

async function onLogout(): Promise<void> {
  await logout();
  const email = await getLoggedInEmail();
  const inputEmail = document.getElementById("input-email") as HTMLInputElement;
  if (email) inputEmail.value = email;
  document.getElementById("view-logged-in").style.display = "none";
  document.getElementById("view-login").style.display = "block";
}

function showLoggedIn(email: string | null): void {
  document.getElementById("view-login").style.display = "none";
  document.getElementById("view-logged-in").style.display = "block";
  document.getElementById("user-email").textContent = email ?? "";
}

function toggleFn(id: string): void {
  const body = document.getElementById(id);
  const arrow = document.getElementById(`arr-${id}`);
  const open = body.style.display !== "none";
  body.style.display = open ? "none" : "block";
  arrow.classList.toggle("open", !open);
}

async function insertFormula(formula: string): Promise<void> {
  try {
    await Excel.run(async (context) => {
      const cell = context.workbook.getActiveCell();
      cell.formulas = [[formula]];
      await context.sync();
    });
  } catch (e) {
    console.error("Erro ao inserir fórmula:", e);
  }
}

function showStatus(message: string, type: "ok" | "error" | "info"): void {
  const bar = document.getElementById("status-bar");
  const icon = document.getElementById("status-icon");
  const text = document.getElementById("status-text");
  const icons = { ok: "✓", error: "✗", info: "…" };
  bar.className = `cog-status cog-status--${type}`;
  icon.textContent = icons[type];
  text.textContent = message;
  bar.style.display = "flex";
}

function hideStatus(): void {
  document.getElementById("status-bar").style.display = "none";
}
